import { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { X, CreditCard, Loader2 } from 'lucide-react';
import { colors } from '../constants/colors';
import { createSetupIntent, savePaymentMethod } from '../api/client';

import { getStripeKey } from '../config/env';
const STRIPE_PUBLISHABLE_KEY = getStripeKey();
const stripePromise = STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null;

// Debug logging for Stripe configuration
console.log('[Stripe] Publishable key configured:', STRIPE_PUBLISHABLE_KEY ? `${STRIPE_PUBLISHABLE_KEY.substring(0, 12)}...` : 'MISSING');
console.log('[Stripe] stripePromise:', stripePromise ? 'initialized' : 'null');

interface AddCardModalProps {
  onClose: () => void;
  onSaved: () => void;
}

function AddCardForm({ onClose, onSaved }: AddCardModalProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      setError('Stripe not loaded');
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setError('Card element not found');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Create SetupIntent on server
      const { clientSecret } = await createSetupIntent();

      // Confirm card setup
      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardElement,
        },
      });

      if (stripeError) {
        setError(stripeError.message || 'Failed to add card');
        return;
      }

      if (setupIntent?.payment_method) {
        // Save payment method to our backend
        await savePaymentMethod(setupIntent.payment_method as string);
        onSaved();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add card');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={styles.form}>
      <div style={styles.cardElementWrapper}>
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: colors.ink.black,
                '::placeholder': {
                  color: colors.gray[400],
                },
              },
              invalid: {
                color: '#DC2626',
              },
            },
          }}
        />
      </div>

      {error && <div style={styles.error}>{error}</div>}

      <div style={styles.actions}>
        <button
          type="button"
          onClick={onClose}
          style={styles.cancelButton}
          disabled={isLoading}
        >
          Cancel
        </button>
        <button
          type="submit"
          style={styles.submitButton}
          disabled={!stripe || isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
              Saving...
            </>
          ) : (
            'Add Card'
          )}
        </button>
      </div>
    </form>
  );
}

export function AddCardModal({ onClose, onSaved }: AddCardModalProps) {
  if (!stripePromise) {
    return (
      <div style={styles.overlay} onClick={onClose}>
        <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
          <div style={styles.header}>
            <h2 style={styles.title}>Add Payment Method</h2>
            <button style={styles.closeButton} onClick={onClose}>
              <X size={20} />
            </button>
          </div>
          <div style={styles.content}>
            <div style={styles.error}>
              Payment system not configured. Please contact support.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <div style={styles.headerIcon}>
            <CreditCard size={24} color={colors.brand.blue} />
          </div>
          <div>
            <h2 style={styles.title}>Add Payment Method</h2>
            <p style={styles.subtitle}>Your card will be saved securely for future purchases</p>
          </div>
          <button style={styles.closeButton} onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        <div style={styles.content}>
          <Elements stripe={stripePromise}>
            <AddCardForm onClose={onClose} onSaved={onSaved} />
          </Elements>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
  },
  modal: {
    backgroundColor: colors.white,
    borderRadius: '16px',
    width: '100%',
    maxWidth: '480px',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '20px 20px 0 20px',
  },
  headerIcon: {
    width: '48px',
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.brand.blue10,
    borderRadius: '12px',
    flexShrink: 0,
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 600,
    color: colors.ink.black,
  },
  subtitle: {
    margin: '4px 0 0 0',
    fontSize: '14px',
    color: colors.gray[600],
  },
  closeButton: {
    marginLeft: 'auto',
    padding: '8px',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    color: colors.gray[400],
  },
  content: {
    padding: '20px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  cardElementWrapper: {
    padding: '14px',
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: '10px',
    backgroundColor: colors.white,
  },
  error: {
    padding: '12px',
    backgroundColor: '#FEE2E2',
    color: '#DC2626',
    borderRadius: '8px',
    fontSize: '14px',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    marginTop: '8px',
  },
  cancelButton: {
    flex: 1,
    padding: '14px',
    fontSize: '15px',
    fontWeight: 600,
    color: colors.gray[700],
    backgroundColor: colors.gray[100],
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
  },
  submitButton: {
    flex: 1,
    padding: '14px',
    fontSize: '15px',
    fontWeight: 600,
    color: colors.white,
    backgroundColor: colors.brand.blue,
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
};
